import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";

import { isRunningChatStatus } from "@intelligems/ig-types/schemas/ai/ai-chat-state";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { chatHistoryQueryOptions } from "@src/api/ai/chat-history/use-get-chat-history";
import { useDispatch } from "@src/store";
import { restoreChatState } from "@src/store/slices/aiChat";

import { useExecutionPanelContext } from "./ExecutionPanelContext";
import { waitForSettledTurn } from "./interruptedTurn";

/**
 * Docked above the composer while the chat holds a turn nothing on this page
 * is streaming (`AiChatState.interrupted`) — a reload mid-response, or a
 * dropped stream. The composer stays usable beside it, so the merchant can
 * also just say something new.
 *
 * Continue picks the turn up from what the server saved, on the same session.
 * The server keeps running a turn after the page let go of it, so the saved
 * state is re-read — and, while the server is still on that turn, re-read
 * until it settles — rather than resumed: a second run on the same session is
 * refused. A settled turn's answer is simply shown; only a turn the server
 * abandoned (it never settles) is re-entered, through `onSend.resume` when the
 * chat offers one.
 *
 * Styled as the dock's other cards (12px radius, Border/Core/Disabled
 * hairline, low shadow). No Figma exists for this state.
 */
export const InterruptedTurnNotice = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { aiChat, onSend } = useExecutionPanelContext();
  const [continuing, setContinuing] = useImmer(false);
  // Continue found the server still working on the turn and is waiting for it
  // to settle rather than starting a second run.
  const [awaitingServerTurn, setAwaitingServerTurn] = useImmer(false);
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  const continueTurn = async () => {
    const { sessionId } = aiChat;
    setContinuing(() => true);
    try {
      const saved = await waitForSettledTurn(
        async () =>
          (await queryClient.fetchQuery({
            ...chatHistoryQueryOptions({ sessionId }),
            staleTime: 0,
          })) ?? null,
        {
          isCancelled: () => unmounted.current,
          onWaiting: () => setAwaitingServerTurn(() => true),
        }
      );
      if (unmounted.current) {
        return;
      }
      if (saved) {
        dispatch(restoreChatState(saved));
      }
      if (!saved || isRunningChatStatus(saved.status)) {
        onSend.resume?.(sessionId);
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error("[InterruptedTurnNotice] continue failed", error);
    } finally {
      if (!unmounted.current) {
        setContinuing(() => false);
        setAwaitingServerTurn(() => false);
      }
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={3}
      role="status"
      data-testid="interrupted-turn-notice"
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.disabled}`,
        borderRadius: "12px",
        boxShadow: theme.igShadows.low,
        px: 3,
        py: 2,
      }}
    >
      <IgTypography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, lineHeight: "18px" }}
      >
        {awaitingServerTurn
          ? "Still finishing this reply…"
          : "This reply was interrupted."}
      </IgTypography>
      <IgButton
        variant="outlined"
        color="secondary"
        size="small"
        disabled={continuing}
        onClick={() => void continueTurn()}
        sx={{ flexShrink: 0 }}
      >
        Continue
      </IgButton>
    </Stack>
  );
};
